<template>
  <div class="app-container">
    <!-- 新增：明细页组件，通过 v-if 控制显示 -->
    <detail-list
      v-if="showDetail"
      :regions-list="regionsList"
      :responsibility-type-list="responsibilityTypeList.responsibility_type"
      :route-info="detailRouteInfo"
      :status-options="statusOptions"
      @close="showDetail = false"
    />
    <!-- 原有内容包裹在 v-else  中 -->
    <template v-else>
      <!-- 1. 查询条件区  -->
      <el-card class="search-card" shadow="never">
        <el-form :model="queryParams" size="small">
          <el-row :gutter="15">
            <el-col :span="6">
              <el-form-item :label="$t('base.billingMonth')">
                <el-select
                  v-model="queryParams.reconciliationPeriod"
                  :placeholder="$t('fixDutyFine.pleaseSelect')"
                  style="width: 100%"
                >
                  <el-option
                    v-for="(item, index) in accessiblePeriodList"
                    :key="index"
                    :label="item"
                    :value="item"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item :label="$t('fixDutyFine.region')">
                <el-select
                  v-model="queryParams.regionId"
                  :placeholder="$t('fixDutyFine.pleaseSelect')"
                  style="width: 100%"
                >
                  <el-option :label="$t('fixDutyFine.allRegions')" :value="null" />
                  <el-option
                    v-for="item in regionsList"
                    :key="item.id"
                    :label="getRegionName(item)"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item :label="$t('fixDutyFine.penaltyType')">
                <el-select
                  v-model="queryParams.responsibilityType"
                  :placeholder="$t('collectionCenter.tudo')"
                  clearable
                  filterable
                  style="width: 100%"
                >
                  <el-option
                    v-for="item in responsibilityTypeList.responsibility_type"
                    :key="item.key"
                    :label="item.value"
                    :value="item.key"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item :label="$t('fixDutyFine.processStatus')">
                <el-select
                  v-model="queryParams.status"
                  :placeholder="$t('fixDutyFine.pleaseSelect')"
                  style="width: 100%"
                >
                  <el-option
                    v-for="item in statusOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="15">
            <el-col :span="6">
              <el-form-item :label="$t('fixDutyFine.waybillNumber')">
                <el-input
                  v-model="queryParams.waybillNumber"
                  :placeholder="$t('fixDutyFine.waybillPlaceholder')"
                  :rows="1"
                  style="width: 100%"
                  type="textarea"
                />
              </el-form-item>
            </el-col>
            <el-col :span="18" style="margin-top: 29px">
              <el-form-item>
                <el-button icon="el-icon-search" type="primary" @click="handleQuery">{{ $t('fixDutyFine.query') }}
                </el-button>
                <el-button icon="el-icon-refresh" @click="resetQuery">{{ $t('fixDutyFine.reset') }}
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>
      <!-- 2. 全局概览区 (卡片) -->
      <div class="overview-row">
        <div
          v-for="(card, index) in overviewCards"
          :key="index"
          :class="`card-${card.type}`"
          class="overview-card"
        >
          <div class="card-header">
            <span class="card-title">{{ card.title }}</span>
            <i :class="card.icon" class="card-icon" />
          </div>
          <div class="card-value">{{ card.value }}</div>
          <div
            :class="{
              'text-green': card.isGrowth,
              'text-red': !card.isGrowth,
              'text-orange': card.isWarning
            }"
            class="card-sub"
          >
            <i v-if="card.subIcon" :class="card.subIcon" /> {{ card.sub }}
          </div>
        </div>
      </div>
      <!-- 3. 列表区 -->
      <el-card class="table-card" shadow="never">
        <div slot="header" class="clearfix">
          <span class="table-title">{{ $t('fixDutyFine.regionPenaltySummary') }}</span>
          <span class="table-sub-title">{{ $t('fixDutyFine.summaryTip') }}</span>
        </div>
        <el-table
          :data="tableData"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
          border
          style="width: 100%"
        >
          <el-table-column
            :label="$t('base.billingMonth')"
            align="left"
            prop="reconciliationPeriod"
            width="120"
          />
          <el-table-column
            :label="$t('fixDutyFine.region')" align="left" prop="zhName"
            width="100"
          >
            <template slot-scope="scope">
              <span class="bold-text">{{ getRegionName(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('fixDutyFine.confirmedLost')"
            align="center"
            prop="confirmedLostCount"
          >
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'confirmLoss')"
              >{{ formatNumber(scope.row.confirmedLostCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('fixDutyFine.StolenOrRobbed')"
            align="center"
            prop="stolenOrRobbedCount"
          >
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'preCheck')"
              >{{ formatNumber(scope.row.stolenOrRobbedCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('fixDutyFine.Damage')" align="center" prop="damagedCount">
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'batter')"
              >{{ formatNumber(scope.row.damagedCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('fixDutyFine.timeoutNoClosure')"
            align="center"
            prop="timeoutNoClosureCount"
          >
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'overdue')"
              >{{ formatNumber(scope.row.timeoutNoClosureCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('fixDutyFine.complaintWorkOrder')"
            align="center"
            prop="complaintWorkOrderCount"
          >
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'complaint')"
              >{{ formatNumber(scope.row.complaintWorkOrderCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('fixDutyFine.SocialMediaUpgradeComplaint')"
            align="center"
            prop="socialMediaCount"
          >
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'escalation')"
              >{{ formatNumber(scope.row.socialMediaCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('fixDutyFine.timeoutReturn')"
            align="center"
            prop="timeoutReturnCount"
          >
            <template slot-scope="scope">
              <span
                class="clickable-text green-bold"
                @click="handleDrillDown(scope.row, 'timeout')"
              >{{ formatNumber(scope.row.timeoutReturnCount) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('fixDutyFine.acceptedAmount')"
            align="right"
            prop="acceptedAmount"
          >
            <template slot-scope="scope">{{ formatCurrency(scope.row.acceptedAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('fixDutyFine.pending')" align="center" prop="pendingCount">
            <template slot-scope="scope">
              <span class="pending-pill">{{ formatNumber(scope.row.pendingCount) }}
                {{ $t('fixDutyFine.ticketsUnit') }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('fixDutyFine.completionRate')"
            align="center"
            prop="completionRate"
            width="180"
          >
            <template slot-scope="scope">
              <div class="progress-cell">
                <el-progress
                  :color="scope.row.completionRate >= 80 ? '#67c23a' : '#e6a23c'"
                  :percentage="scope.row.completionRate"
                  :show-text="false"
                  :stroke-width="8"
                />
                <span class="progress-text">{{ scope.row.completionRate.toFixed(1) }}%</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<script>
import {
  accessiblePeriods,
  accessibleRegions,
  getSummaryOverview,
  getSummaryPage,
  lmLossResponsibilityGetDropdownOptions
} from '@/api/definiteResponsibilityAndPenalty';

export default {
  name: 'PenaltyBillManagement',
  components: {
    detailList: () => import('./component/DetailList')
  },
  data() {
    return {
      // 新增状态下拉配置
      statusOptions: [
        { label: this.$t('fixDutyFine.allStatus'), value: null }, // 0 代表全部，具体按后端约定
        { label: this.$t('fixDutyFine.pending'), value: 1 },
        { label: this.$t('fixDutyFine.accepted'), value: 2 },
        { label: this.$t('fixDutyFine.pendingReview'), value: 3 },
        { label: this.$t('fixDutyFine.exempted'), value: 4 },
        { label: this.$t('fixDutyFine.maintainOriginal'), value: 5 }
      ],
      // 查询参数
      queryParams: {
        reconciliationPeriod: '2026-08', // 账单月份
        regionId: null, // 大区ID（单选）
        responsibilityType: null, // 扣罚类型（多选，初始为空数组）
        status: null, // 处理状态（单选）
        waybillNumber: '' // 运单号搜索
      },
      // 概览卡片数据（由接口渲染，清空硬编码）
      overviewCards: [],
      // 表格硬编码数据
      tableData: [],
      responsibilityTypeList: [],
      regionsList: [],
      accessiblePeriodList: [], // 账期
      showDetail: false, // 控制页面切换
      detailRouteInfo: {} // 传给子组件的路由信息
    };
  },
  created() {
    this.getReconciliationPeriod();
    // 定责类型
    this.getResponsibilityTypeList();
    this.getAllRegions();
  },
  methods: {
    // 获取账期
    async getReconciliationPeriod() {
      const res = await accessiblePeriods();
      console.log(res, 'accessiblePeriods');
      this.accessiblePeriodList = res.data || [];
      if (this.accessiblePeriodList.length > 0) {
        this.queryParams.reconciliationPeriod = this.accessiblePeriodList[0];
        this.globalOverview();
        this.listQuery();
      }
    },
    // 统一组装查询参数（将前端筛选条件转换为后端接口需要的格式）
    buildParams() {
      // 处理运单号：去除首尾空白，按换行符拆分，过滤掉空行，最多截取2000个
      const waybillStr = this.queryParams.waybillNumber || '';
      const waybillList = waybillStr
        .split('\n') // 按换行拆分
        .map((item) => item.trim()) // 去除单号两端的空格
        .filter((item) => item !== '') // 过滤掉空行
        .slice(0, 2000); // 最多2000个
      return {
        reconciliationPeriod: this.queryParams.reconciliationPeriod || '', // 月份
        regionIds: this.queryParams.regionId ? [this.queryParams.regionId] : [], // 大区单选转数组
        responsibilityType: this.queryParams.responsibilityType || null,
        statusList:
          this.queryParams.status !== null && this.queryParams.status !== ''
            ? [this.queryParams.status]
            : [], // 处理状态单选转数组
        waybillNumbers: waybillList // 运单号数组（最多2000个）
      };
    },
    // 全局概览
    globalOverview() {
      const data = this.buildParams(); // 使用统一方法
      getSummaryOverview(data).then((res) => {
        if (res.code === 200) {
          const d = res.data || {};

          // 动态组装概览卡片
          this.overviewCards = [
            {
              title: this.$t('fixDutyFine.totalResponsibilityTickets'),
              value: this.formatNumber(d.totalTickets), // 37
              sub:
                d.momChange !== null && d.momChange !== undefined
                  ? `${this.$t('fixDutyFine.comparedLastPeriod')} ${d.momChange >= 0 ? '+' : ''}${
                    d.momChange
                  }%`
                  : `${this.$t('fixDutyFine.comparedLastPeriod')} --`,
              icon: 'el-icon-data-line',
              type: 'green',
              isGrowth: d.momChange >= 0
            },
            {
              title: this.$t('fixDutyFine.pending'),
              value: this.formatNumber(d.pendingCount), // 35
              sub: this.$t('fixDutyFine.pleaseCompleteBeforeDeadline'),
              icon: 'el-icon-time',
              type: 'orange',
              isWarning: true
            },
            {
              title: this.$t('fixDutyFine.accepted'),
              value: this.formatNumber(d.acceptedCount), // 0
              sub: `${this.$t('fixDutyFine.ratio')} ${
                d.acceptedRatio ? d.acceptedRatio.toFixed(2) : 0
              }%`,
              icon: 'el-icon-circle-check',
              type: 'green',
              isGrowth: true
            },
            {
              title: this.$t('fixDutyFine.appealed'),
              value: this.formatNumber(d.appealedCount), // 2
              sub: `${this.$t('fixDutyFine.ratio')} ${
                d.appealedRatio ? d.appealedRatio.toFixed(2) : 0
              }%`,
              icon: 'el-icon-warning-outline',
              type: 'red',
              isGrowth: false
            },
            {
              title: this.$t('fixDutyFine.processingCompletionRate'),
              value: `${d.completionRate ? d.completionRate.toFixed(2) : 0}%`,
              sub: this.$t('fixDutyFine.targetGte95'),
              icon: 'el-icon-pie-chart',
              type: 'purple',
              isWarning: d.completionRate < 95
            },
            {
              title: this.$t('fixDutyFine.pendingHeadquartersReview'),
              value: this.formatNumber(d.pendingReviewCount), // 1
              sub: `${this.$t('fixDutyFine.from')} ${d.pendingReviewRegionCount} ${this.$t(
                'fixDutyFine.regionsUnit'
              )}`,
              icon: 'el-icon-office-building',
              type: 'orange',
              isWarning: true
            },
            {
              title: this.$t('fixDutyFine.reviewed'),
              value: this.formatNumber(d.reviewedCount), // 1
              sub: `${this.$t('fixDutyFine.periodCompletionRate')} ${
                d.reviewedRatio ? d.reviewedRatio.toFixed(2) : 0
              }%`,
              icon: 'el-icon-circle-check',
              type: 'green',
              isGrowth: true
            },
            {
              title: this.$t('fixDutyFine.determinedExempted'),
              value: this.formatNumber(d.exemptedCount), // 0
              sub: `${this.$t('fixDutyFine.exemptionRate')} ${
                d.exemptedRatio ? d.exemptedRatio.toFixed(2) : 0
              }%`,
              icon: 'el-icon-check',
              type: 'purple',
              isGrowth: true
            },
            {
              title: this.$t('fixDutyFine.maintainResponsibility'),
              value: this.formatNumber(d.maintainedCount), // 1
              sub: `${this.$t('fixDutyFine.maintenanceRate')} ${
                d.maintainedRatio ? d.maintainedRatio.toFixed(2) : 0
              }%`,
              icon: 'el-icon-document-checked',
              type: 'orange',
              isWarning: true
            },
            {
              title: this.$t('fixDutyFine.avgReviewDuration'),
              value: d.avgReviewDuration
                ? `${d.avgReviewDuration}${this.$t('fixDutyFine.daysUnit')}`
                : '--',
              sub: this.$t('fixDutyFine.targetLte2Days'),
              icon: 'el-icon-stopwatch',
              type: 'green',
              isGrowth: true
            }
          ];
        }
      });
    },
    // 列表区
    listQuery() {
      const data = {
        pageNumber: 1,
        pageSize: 120,
        params: this.buildParams() // 使用统一方法
      };
      getSummaryPage(data).then((res) => {
        if (res.code === 200) {
          console.log(res.data, 'getSummaryOverview');
          // 直接使用后端接口返回的 records 数据
          this.tableData = res.data.records || [];
        }
      });
    },
    // 新增：下钻跳转方法
    handleDrillDown(row, typeKey) {
      console.log(row, 'handleDrillDown');
      const typeMap = {
        confirmLoss: this.$t('fixDutyFine.confirmedLost'),
        preCheck: this.$t('fixDutyFine.StolenOrRobbed'),
        batter: this.$t('fixDutyFine.Damage'),
        overdue: this.$t('fixDutyFine.timeoutNoClosure'),
        complaint: this.$t('fixDutyFine.complaintWorkOrder'),
        escalation: this.$t('fixDutyFine.SocialMediaUpgradeComplaint'),
        timeout: this.$t('fixDutyFine.timeoutReturn'),
        pending: this.$t('fixDutyFine.pending')
      };
      // 各列对应的扣罚类型（数组）：投诉工单 = 5 超时未回复 + 6 无效回复
      const typeParamMap = {
        confirmLoss: ['1'], // 确认丢失
        preCheck: ['2'], // 被抢或被盗
        batter: ['3'], // 包裹破损
        overdue: ['8'], // 超时未闭环
        complaint: ['5', '6'], // 投诉工单-超时未回复 / 投诉工单-无效回复
        escalation: ['7'], // 社媒升级投诉
        timeout: ['10'] // 退货超时未达
      };
      // 组装传给子组件的数据 (改用后端字段)
      this.detailRouteInfo = {
        regionIds: [row.regionId],
        responsibilityTypeList: typeParamMap[typeKey] || [], // 扣罚类型（多选）
        statusList: [],
        waybillNumbers: [],
        reconciliationPeriod: row.reconciliationPeriod, // 后端字段
        regionName: this.getRegionName(row), // 按当前语言取大区名称
        type: typeMap[typeKey] || this.$t('fixDutyFine.allTypes')
      };
      // 切换视图
      this.showDetail = true;
    },
    handleQuery() {
      this.globalOverview();
      this.listQuery();
    },
    // 获取定责类型
    getResponsibilityTypeList() {
      lmLossResponsibilityGetDropdownOptions().then((res) => {
        if (res.code === 200) {
          this.responsibilityTypeList = res.data;
        }
      });
    },
    // 根据当前语言显示大区名称：中文取 zhName，葡语取 puName
    getRegionName(region) {
      if (!region) return '';
      const { zhName, puName, regionName } = region;
      return this.$i18n.locale === 'zh'
        ? zhName || puName || regionName || ''
        : puName || zhName || regionName || '';
    },
    // 获取所有 大区
    getAllRegions() {
      accessibleRegions().then((res) => {
        if (res.code === 200) {
          console.log(res.data, 'accessibleRegions');
          // 同时保留 zhName / puName，由 getRegionName 按当前语言取值
          this.regionsList = res.data || [];
        }
      });
    },
    resetQuery() {
      this.queryParams = {
        reconciliationPeriod: '2026-08',
        regionId: null, // 对应大区
        responsibilityType: null, // 对应扣罚类型（多选清空为数组）
        status: null, // 对应处理状态
        waybillNumber: '' // 对应运单号
      };
      if (this.accessiblePeriodList.length > 0) {
        this.queryParams.reconciliationPeriod = this.accessiblePeriodList[0];
        this.globalOverview();
        this.listQuery();
      }
      this.handleQuery(); // 重置后刷新
    },
    // 需求：千分位格式化
    formatNumber(num) {
      if (num === null || num === undefined) return '--';
      return Number(num)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    // 需求：金额格式化 R$ 1,234.56
    formatCurrency(num) {
      if (num === 0 || num === null || num === undefined) return '--';
      return (
        'R$ ' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.search-card {
  margin-bottom: 20px;

  ::v-deep .el-form-item__label {
    font-weight: bold;
  }
}

/* 概览卡片栅格：使用 grid 固定每行 6 列且卡片等高，
   避免较长文案（如葡语）换行导致卡片高度不一致、布局错位 */
.overview-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 20px 15px;
  margin-bottom: 20px;
}

/* 概览卡片样式 */

.overview-card {
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  display: flex;
  flex-direction: column;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .card-title {
      font-size: 13px;
      color: #606266;
    }

    .card-icon {
      font-size: 18px;
    }
  }

  .card-value {
    font-size: 28px;
    font-weight: bold;
    color: #303133;
    margin-bottom: 8px;
  }

  .card-sub {
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;

    &.text-green {
      color: #67c23a;
    }

    &.text-red {
      color: #f56c6c;
    }

    &.text-orange {
      color: #e6a23c;
    }
  }

  /* 图标颜色分类 (还原图片大概颜色) */
  &.card-green .card-icon {
    color: #67c23a;
  }

  &.card-orange .card-icon {
    color: #e6a23c;
  }

  &.card-red .card-icon {
    color: #f56c6c;
  }

  &.card-purple .card-icon {
    color: #9254de;
  }
}

/* 表格卡片样式 */
.table-card {
  .table-title {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
  }

  .table-sub-title {
    font-size: 12px;
    color: #909399;
    margin-left: 12px;
  }
}

/* 表格内部特殊样式 */
.clickable-text {
  color: #409eff;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &.red-text {
    color: #f56c6c;
  }
}

/* 如果想要胶囊背景效果 */
.pill-bg {
  background-color: #fdf6ec; /* 浅橙色背景 */
  color: #67c23a;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 10px;
}

.progress-cell {
  display: flex;
  align-items: center;

  .el-progress {
    width: 80px;
    margin-right: 8px;
  }

  .progress-text {
    font-size: 12px;
    color: #606266;
  }
}

/* 大区加粗 */
.bold-text {
  font-weight: bold;
  color: #303133; /* 保持原有的深色 */
}

/* 数据计数：绿色加粗 */
.green-bold {
  color: #08a65a; /* Element UI 标准绿，和进度条颜色保持一致 */
  font-weight: bold;
  font-size: 15px; /* 稍微放大一点点，凸显数据 */
}

/* 待处理 - 橙色胶囊样式 */
.pending-pill {
  display: inline-block;
  background-color: #fdf6ec; /* 极浅的橙色背景 */
  color: #e6a23c; /* 橙色文字 */
  font-weight: bold; /* 字体加粗 */
  padding: 2px 10px;
  border-radius: 12px; /* 圆角胶囊效果 */
  font-size: 13px;
}
</style>
